import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";
import BasicButton from "../basics/BasicButton";
import {
  EVENT_DISPATCH_SINK_OPTIONS,
  type EventDispatchSinkId,
  useGetEventDispatchConfigQuery,
  usePutEventDispatchConfigMutation,
} from "../../services/rtkApi/eventDispatchAdminApi";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #e8e0ff;
  padding: 20px;
  min-height: 100vh;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  overflow: hidden;
`;

const Scroll = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;

const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }
  th {
    font-weight: 600;
    color: #333;
    position: sticky;
    top: 0;
    background: #fafafa;
    z-index: 1;
  }
  th.event-col {
    min-width: 200px;
    position: sticky;
    left: 0;
    z-index: 2;
    background: #f5f5f5;
  }
  td.event-col {
    position: sticky;
    left: 0;
    background: #fff;
    font-family: ui-monospace, monospace;
    white-space: nowrap;
  }
`;

const Hint = styled.p`
  font-size: 0.85rem;
  color: #555;
  margin: 0 0 12px;
  line-height: 1.4;
`;

function cloneRules(
  src: Record<string, EventDispatchSinkId[]>,
): Record<string, EventDispatchSinkId[]> {
  const out: Record<string, EventDispatchSinkId[]> = {};
  for (const [k, v] of Object.entries(src)) {
    out[k] = [...v];
  }
  return out;
}

function mergeCodeDefaults(
  eventTypes: string[],
  codeDefaults: Partial<Record<string, EventDispatchSinkId[]>>,
): Record<string, EventDispatchSinkId[]> {
  const out: Record<string, EventDispatchSinkId[]> = {};
  for (const t of eventTypes) {
    out[t] = [...(codeDefaults[t] ?? [])];
  }
  return out;
}

const EventsAdmin: React.FC = () => {
  const user = useAppSelector((state) => state.auth);
  const isAdmin = checkRole(user.role ?? "", "admin");

  const { data, isLoading, error, refetch } = useGetEventDispatchConfigQuery(undefined, {
    skip: !isAdmin || user.bootstrapStatus !== "ready",
  });

  const [putConfig, { isLoading: saving }] = usePutEventDispatchConfigMutation();

  const [draft, setDraft] = useState<Record<string, EventDispatchSinkId[]> | null>(null);

  useEffect(() => {
    if (data?.success && data.data?.effective) {
      setDraft(cloneRules(data.data.effective as Record<string, EventDispatchSinkId[]>));
    }
  }, [data]);

  const meta = data?.success ? data.data : null;

  const tableReady = useMemo(() => Boolean(meta?.eventTypes?.length && draft), [meta, draft]);

  const toggleSink = (eventType: string, sink: EventDispatchSinkId) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = cloneRules(prev);
      const row = [...(next[eventType] ?? [])];
      const i = row.indexOf(sink);
      if (i >= 0) {
        row.splice(i, 1);
      } else {
        row.push(sink);
      }
      next[eventType] = row;
      return next;
    });
  };

  const handleSave = async () => {
    if (!draft || !meta?.eventTypes.length) {
      return;
    }
    try {
      await putConfig({ rules: draft }).unwrap();
      toast.success("Event dispatch rules saved");
      refetch();
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "data" in e
          ? JSON.stringify((e as { data: unknown }).data)
          : String(e);
      toast.error(`Save failed: ${msg}`);
    }
  };

  const handleResetDefaults = () => {
    if (!meta?.eventTypes) {
      return;
    }
    setDraft(mergeCodeDefaults(meta.eventTypes, meta.codeDefaults ?? {}));
    toast.info("Reverted draft to built-in code defaults (save to apply)");
  };

  if (!isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (user.bootstrapStatus !== "ready") {
    return (
      <PageContainer className="evadm-page">
        <p>Loading session…</p>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer className="evadm-page">
        <p>Loading event dispatch config…</p>
      </PageContainer>
    );
  }

  if (error || !meta) {
    return (
      <PageContainer className="evadm-page">
        <p>Failed to load config.</p>
        <BasicButton text="Retry" onClick={() => void refetch()} />
      </PageContainer>
    );
  }

  return (
    <PageContainer className="evadm-page">
      <style>{`
        @media (max-width: 700px) {
          .evadm-page { padding: 12px 8px !important; }
          .evadm-row { flex-wrap: wrap !important; gap: 8px !important; }
          .evadm-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 0 -8px; }
        }
      `}</style>
      <div className="evadm-row" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>Platform event dispatch</h1>
        <Link to="/admin">← Admin home</Link>
      </div>
      <Hint>
        Choose which <strong>outbound sinks</strong> run for each platform event. User notify uses messenger (Telegram +
        push). Respect, game scoring, and card effects are unchanged — only notification-style sinks are configured here.
        Broadcast is gated in code (requires safe metadata). Twitter is a placeholder.
      </Hint>
      {meta.updatedAt && (
        <p style={{ fontSize: "0.85rem", color: "#444" }}>
          Last saved: {meta.updatedAt}
          {meta.updatedByUsername ? ` · ${meta.updatedByUsername}` : ""}
        </p>
      )}

      <Card>
        <div className="evadm-row" style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <BasicButton
            text={saving ? "Saving…" : "Save to database"}
            onClick={() => void handleSave()}
            disabled={!tableReady || saving}
          />
          <BasicButton
            text="Reset draft to code defaults"
            onClick={handleResetDefaults}
            bgColor="#ddd"
            hoverBgColor="#ccc"
          />
          <BasicButton
            text="Reload from server"
            onClick={() => void refetch()}
            bgColor="#ddd"
            hoverBgColor="#ccc"
          />
        </div>

        {tableReady && draft && (
          <Scroll className="evadm-table-wrap">
            <Table>
              <thead>
                <tr>
                  <th className="event-col">Event type</th>
                  {EVENT_DISPATCH_SINK_OPTIONS.map((s) => (
                    <th key={s.id} title={s.hint}>
                      <div>{s.label}</div>
                      <div style={{ fontWeight: 400, fontSize: "0.7rem", color: "#666" }}>{s.id}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {meta.eventTypes.map((eventType) => (
                  <tr key={eventType}>
                    <td className="event-col">{eventType}</td>
                    {EVENT_DISPATCH_SINK_OPTIONS.map((s) => (
                      <td key={`${eventType}-${s.id}`} style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={(draft[eventType] ?? []).includes(s.id)}
                          onChange={() => toggleSink(eventType, s.id)}
                          aria-label={`${eventType} — ${s.label}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroll>
        )}
      </Card>
    </PageContainer>
  );
};

export default EventsAdmin;
